import React, { useMemo, useState, FormEvent } from "react";
import styles from "./HomeIntroHero.module.css";
import EnvelopeSolid from "Utils/Assets/SVGs/EnvelopeSolid";
import { NewsletterService } from "../../../Logic/NewsletterService";
import { useAppSelector } from "Data/Store/hooks";
import { useNewsletterEmailPrefill } from "../../../Logic/Functions/Hooks/useNewsletterEmailPrefill";

const HomeNewsletterForm: React.FC = () => {
  const [email, setEmail] = useState("");
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const newsletterService = new NewsletterService();

  const authEmail = useAppSelector(
    (s) => s.userAuthenticationReducer?.user?.email ?? ""
  );
  const token = useAppSelector(
    (s) => s.userAuthenticationReducer?.token?.trim() ?? ""
  );
  const isLoggedIn = !!token;

  const { handleChange, resetPrefillGuards } = useNewsletterEmailPrefill({
    isLoggedIn,
    authEmail,
    email,
    setEmail,
    setApiError,
    setSuccessMsg,
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setApiError(null);
    setSuccessMsg(null);
    const msg = await newsletterService.handleSubscribe({
      email,
      setLoading: setSubmitting,
      setApiError,
    });
    if (!msg) return;
    setSuccessMsg(msg);
    setEmail("");
    resetPrefillGuards();
  };

  return (
    <section
      className={styles.HNSection}
      aria-labelledby="home-newsletter-title"
      data-testid="home-newsletter-section"
    >
      <header className={styles.HNHeader}>
        <h2 id="home-newsletter-title" className={styles.HNTitle}>
          Stay ahead of what&apos;s next
        </h2>
        <p className={styles.HNSubtitle}>
          Monthly insights on smart buildings, lab connectivity, and automation
          best practices.
        </p>
      </header>

      <form
        className={styles.HNForm}
        onSubmit={handleSubmit}
        noValidate
        data-testid="home-newsletter-form"
      >
        <div className={styles.HNInputShell}>
          <span className={styles.HNInputIcon}>
            <EnvelopeSolid className={styles.HNInputIconSvg} />
          </span>

          <input
            type="email"
            className={styles.HNInput}
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => handleChange(e.target.value)}
            aria-invalid={!!apiError}
            data-testid="home-newsletter-input"
          />
        </div>

        <button
          type="submit"
          className={styles.HNButton}
          data-testid="home-newsletter-submit"
          disabled={submitting}
        >
          {submitting ? "Subscribing..." : "Subscribe"}
        </button>
      </form>

      {apiError ? (
        <p className={styles.formError} data-testid="home-newsletter-error" role="alert">
          {apiError}
        </p>
      ) : successMsg ? (
        <p className={styles.HNHelper} data-testid="home-newsletter-success" role="status">
          {successMsg}
        </p>
      ) : (
        <p className={styles.HNHelper} data-testid="home-newsletter-helper">
          We respect your inbox. No spam, unsubscribe anytime.
        </p>
      )}
    </section>
  );
};

export default HomeNewsletterForm;
